"use client";

import type { CSSProperties, HTMLAttributes } from "react";
import * as React from "react";
import { cn } from "../../lib";

const MAX_ITEMS = 3;

export interface FolderProps extends HTMLAttributes<HTMLDivElement> {
	color?: string;
	size?: number;
	items?: React.ReactNode[];
}

function darkenColor(hex: string, percent: number): string {
	let color = hex.startsWith("#") ? hex.slice(1) : hex;
	if (color.length === 3) {
		color = color
			.split("")
			.map((c) => c + c)
			.join("");
	}
	const num = Number.parseInt(color, 16);
	if (Number.isNaN(num)) {
		return "#5227FF";
	}
	let r = (num >> 16) & 0xff;
	let g = (num >> 8) & 0xff;
	let b = num & 0xff;
	r = Math.max(0, Math.min(255, Math.floor(r * (1 - percent))));
	g = Math.max(0, Math.min(255, Math.floor(g * (1 - percent))));
	b = Math.max(0, Math.min(255, Math.floor(b * (1 - percent))));
	return `#${[r, g, b]
		.map((channel) => channel.toString(16).padStart(2, "0"))
		.join("")
		.toUpperCase()}`;
}

function getOpenTransform(index: number): string {
	if (index === 0) {
		return "translate(-120%, -70%) rotate(-15deg)";
	}
	if (index === 1) {
		return "translate(10%, -70%) rotate(15deg)";
	}
	if (index === 2) {
		return "translate(-50%, -100%) rotate(5deg)";
	}
	return "";
}

export function Folder({
	color = "#5227FF",
	size = 1,
	items = [],
	className,
	style,
	...rest
}: FolderProps) {
	const papersRaw = items.slice(0, MAX_ITEMS);
	const papers: (React.ReactNode | null)[] = [...papersRaw];
	while (papers.length < MAX_ITEMS) {
		papers.push(null);
	}

	const [open, setOpen] = React.useState(false);
	const [paperOffsets, setPaperOffsets] = React.useState<
		{ x: number; y: number }[]
	>(() =>
		Array.from({ length: MAX_ITEMS }, () => ({
			x: 0,
			y: 0,
		})),
	);

	const folderBackColor = darkenColor(color, 0.08);
	const paper1 = darkenColor("#ffffff", 0.1);
	const paper2 = darkenColor("#ffffff", 0.05);
	const paper3 = "#ffffff";

	const toggleFolder = React.useCallback(() => {
		setOpen((prev) => {
			const next = !prev;
			if (!next) {
				setPaperOffsets(
					Array.from({ length: MAX_ITEMS }, () => ({ x: 0, y: 0 })),
				);
			}
			return next;
		});
	}, []);

	const handlePaperMouseMove = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>, index: number) => {
			if (!open) {
				return;
			}
			const rect = e.currentTarget.getBoundingClientRect();
			const centerX = rect.left + rect.width / 2;
			const centerY = rect.top + rect.height / 2;
			const offsetX = (e.clientX - centerX) * 0.15;
			const offsetY = (e.clientY - centerY) * 0.15;
			setPaperOffsets((prev) => {
				const newOffsets = [...prev];
				newOffsets[index] = { x: offsetX, y: offsetY };
				return newOffsets;
			});
		},
		[open],
	);

	const handlePaperMouseLeave = React.useCallback((index: number) => {
		setPaperOffsets((prev) => {
			const newOffsets = [...prev];
			newOffsets[index] = { x: 0, y: 0 };
			return newOffsets;
		});
	}, []);

	const folderVars = {
		"--folder-color": color,
		"--folder-back-color": folderBackColor,
		"--paper-1": paper1,
		"--paper-2": paper2,
		"--paper-3": paper3,
	} as CSSProperties;

	const scaleStyle: CSSProperties = {
		transform: `scale(${size})`,
	};

	return (
		<div
			className={cn(className)}
			style={{
				...scaleStyle,
				...style,
			}}
			{...rest}
		>
			{/* biome-ignore lint/a11y/useSemanticElements: Layered papers + flaps need flow layout; <button> cannot contain these blocks. */}
			<div
				className={cn(
					"group relative cursor-pointer transition-all duration-200 ease-in",
					!open && "hover:-translate-y-2",
				)}
				role="button"
				tabIndex={0}
				aria-expanded={open}
				aria-label="Toggle folder"
				style={{
					...folderVars,
					transform: open ? "translateY(-8px)" : undefined,
				}}
				onClick={toggleFolder}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						toggleFolder();
					}
				}}
			>
				<div
					className="relative h-[80px] w-[100px] rounded-tl-none rounded-tr-[10px] rounded-br-[10px] rounded-bl-[10px]"
					style={{ backgroundColor: folderBackColor }}
				>
					<span
						aria-hidden
						className="absolute bottom-[98%] left-0 z-0 h-[10px] w-[30px] rounded-tl-[5px] rounded-tr-[5px] rounded-br-none rounded-bl-none"
						style={{ backgroundColor: folderBackColor }}
					/>
					{papers.map((item, i) => {
						let sizeClasses = "";
						if (i === 0) {
							sizeClasses = open
								? "h-[80%] w-[70%]"
								: "h-[80%] w-[70%]";
						}
						if (i === 1) {
							sizeClasses = open
								? "h-[80%] w-[80%]"
								: "h-[70%] w-[80%]";
						}
						if (i === 2) {
							sizeClasses = open
								? "h-[80%] w-[90%]"
								: "h-[60%] w-[90%]";
						}

						const transformStyle = open
							? `${getOpenTransform(i)} translate(${paperOffsets[i]?.x ?? 0}px, ${paperOffsets[i]?.y ?? 0}px)`
							: undefined;

						return (
							// biome-ignore lint/a11y/noStaticElementInteractions: Pointer tilt on individual sheets when open.
							<div
								key={`paper-${i}`}
								className={cn(
									"absolute bottom-[10%] left-1/2 z-20 transition-all duration-300 ease-in-out",
									!open
										? "-translate-x-1/2 translate-y-[10%] transform group-hover:translate-y-0"
										: "hover:scale-110",
									sizeClasses,
								)}
								style={{
									...(open
										? { transform: transformStyle }
										: {}),
									backgroundColor:
										i === 0
											? paper1
											: i === 1
												? paper2
												: paper3,
									borderRadius: "10px",
								}}
								onMouseMove={(e) => handlePaperMouseMove(e, i)}
								onMouseLeave={() => handlePaperMouseLeave(i)}
							>
								{item}
							</div>
						);
					})}
					<div
						className={cn(
							"absolute z-30 h-full w-full origin-bottom transition-all duration-300 ease-in-out",
							!open &&
								"group-hover:[transform:skew(15deg)_scaleY(0.6)]",
						)}
						style={{
							backgroundColor: color,
							borderRadius: "5px 10px 10px 10px",
							...(open
								? { transform: "skew(15deg) scaleY(0.6)" }
								: {}),
						}}
					/>
					<div
						className={cn(
							"absolute z-30 h-full w-full origin-bottom transition-all duration-300 ease-in-out",
							!open &&
								"group-hover:[transform:skew(-15deg)_scaleY(0.6)]",
						)}
						style={{
							backgroundColor: color,
							borderRadius: "5px 10px 10px 10px",
							...(open
								? { transform: "skew(-15deg) scaleY(0.6)" }
								: {}),
						}}
					/>
				</div>
			</div>
		</div>
	);
}

export default Folder;
